import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, from, zip } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SocketService } from '../../core/backend-adapter/socket.service';
import { NotifyService } from 'src/app/core/layouts/notifications/notify/notify.service';
import { FILTER_TYPES, IFilterData } from 'src/app/core/feature-modules/table-filter/table-filter.interfaces';
import { SYSTEM_COLORS } from 'src/app/core/feature-modules/whitelabel/style-changer/styles/colors/system-colors.constants';
import {
  IReviewsMetrics,
  IReviewsMetricsResponse,
  IFitersObject,
} from '../review-management.interfaces';

@Component({
  selector: 'app-metrics-tab',
  templateUrl: './metrics-tab.component.html',
  styleUrls: ['./metrics-tab.component.scss'],
})
export class MetricsTabComponent implements OnInit, OnDestroy {
  showOverview = true;
  reviewsData: IReviewsMetrics;
  dailyAvgRatings: any;
  dailyTotalReviews: any;
  SYSTEM_COLORS = SYSTEM_COLORS;
  keyword = '';
  filterData: IFilterData[] = [];
  chipsArray: string[] = [];
  filters: IFitersObject = { DATE: ['Last 12 months'] };
  private selectedDateRange = 'DefaultDate';

  private ngUnsubscribe$ = new Subject<void>();

  constructor(
    private socketService: SocketService,
    private notifyService: NotifyService
  ) {}

  ngOnInit(): void {
    this.initFilterData();
    this.loadLocationTags();
    this.loadReviewsData();
  }

  private initFilterData(): void {
    this.filterData.push(
      {
        type: FILTER_TYPES.MULTI,
        title: 'REVIEWS',
        options: ['Responded to', 'Not yet responded to'],
      },
      {
        type: FILTER_TYPES.MULTI,
        title: 'RATINGS',
        options: ['5', '4', '3', '2', '1'],
      },
      {
        type: FILTER_TYPES.DATE,
        title: 'DATE',
        options: ['This month', 'Last 30 days', 'Last month', 'Year to date', 'Last 12 months', 'All time'],
      },
      {
        type: FILTER_TYPES.SINGLE,
        title: 'REVIEW TEXT',
        options: ['Review includes text', 'Review does not include text'],
      }
    );
  }

  loadLocationTags(): void {
    // TODO: Uncomment when API is ready – get-location-tags-and-counts
    // this.socketService.sendRequest('get-location-tags-and-counts', {}).then(
    //   (repl: any) => {
    //     this.filterData.push({
    //       type: FILTER_TYPES.MULTI,
    //       title: 'TAGS',
    //       options: repl.collection.map((l) => l.tagName),
    //     });
    //   },
    //   () => {}
    // );
    // Dummy TAGS for design check
    this.filterData.push({
      type: FILTER_TYPES.MULTI,
      title: 'TAGS',
      options: ['Downtown', 'Airport', 'Mall', 'Suburb'],
    });
  }

  userSearch(): void {
    this.loadReviewsData();
  }

  onChipsRefresh(filters: IFitersObject): void {
    this.chipsArray = [].concat.apply([], Object.values(filters));
    this.chipsArray = this.chipsArray.filter((e) => e);
    if (!filters.hasOwnProperty('DATE')) {
      this.filters = { ...filters, DATE: ['DefaultDate'] };
    } else if (filters.hasOwnProperty('DATE') && !filters['DATE'].length) {
      this.filters = { ...filters, DATE: ['DefaultDate'] };
    } else {
      this.filters = filters;
    }
    this.loadReviewsData();
  }

  hideOverview(toggle: boolean) {
    this.showOverview = !toggle;
  }

  loadReviewsData(): void {
    const request1$ = from(
      this.socketService.sendRequest('get-review-metrics', {
        includeAllTime: true,
        where: this.buildWhereClause(),
      })
    );
    const request2$ = from(
      this.socketService.sendRequest('get-daily-review-metrics', {
        excludePast30Days: true,
        where: this.buildWhereClause(),
      })
    );
    zip(request1$, request2$)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(
        ([res1, res2]: [IReviewsMetricsResponse, any]) => {
          if (res1['collection']?.[0] || res2['collection']) {
            const dataSet1 =
              res1['collection']?.length && res1['collection'][0];
            this.dailyAvgRatings =
              res2['collection']?.length &&
              res2['collection'].map((el) => ({
                date: el.date,
                value: +el.average_starrating,
              }));
            this.dailyTotalReviews =
              res2['collection']?.length &&
              res2['collection'].map((el) => ({
                date: el.date,
                value: +el.total_reviews,
              }));
            this.reviewsData = dataSet1 ? { ...dataSet1 } : undefined;
          } else {
            this.notifyService.error('There is no data for this account');
          }
        },
        () => {
          this.notifyService.error('There is no data for this account');
        }
      );
  }

  private buildWhereClause(): Array<string> {
    const filterClauses: string[][] = [];
    const tempFilterArr: string[] = [];
    const isFiltersApplied = this.chipsArray.length > 0;

    const pushQueriesToMainArray = () => {
      const filterClause: Array<string> = ['-or', ...tempFilterArr];
      filterClauses.push(filterClause);
      tempFilterArr.length = 0;
    };

    if (isFiltersApplied) {
      for (const [key, value] of Object.entries(this.filters)) {
        if (value?.length > 0 && value[0] != null) {
          switch (key) {
            case 'TAGS':
              value.forEach((tag: string) => tempFilterArr.push(`tags @> '{"${tag}"}'`));
              pushQueriesToMainArray();
              break;
            case 'REVIEWS':
              value.forEach((element: string) => {
                element === 'Responded to'
                  ? tempFilterArr.push('NOT replyComment IS NULL')
                  : tempFilterArr.push('replyComment IS NULL');
              });
              pushQueriesToMainArray();
              break;
            case 'RATINGS':
              value.forEach((element: string) => {
                const rating = parseInt(element, 10);
                if (rating >= 1 && rating <= 5) {
                  tempFilterArr.push(`starRating = ${rating}`);
                }
              });
              pushQueriesToMainArray();
              break;
            case 'DATE':
              value.forEach((element: string) => {
                this.selectedDateRange = element;
                switch (element) {
                  case 'This month':
                    tempFilterArr.push(
                      `extract(month from r.date) = extract(month from current_date) and extract(year from r.date) = extract(year from current_date)`
                    );
                    break;
                  case 'Last 30 days':
                    tempFilterArr.push(`r.date > (now() - interval '30 day')`);
                    break;
                  case 'Last month':
                    tempFilterArr.push(
                      `extract(month from r.date) = extract(month from current_date) - 1 and extract(year from r.date) = extract(year from current_date)`
                    );
                    break;
                  case 'Year to date':
                    tempFilterArr.push(`extract(year from r.date) = extract(year from current_date)`);
                    break;
                  case 'Last 12 months':
                    tempFilterArr.push(`r.date > (now() - interval '12 month')`);
                    break;
                  case 'All time':
                    break;
                  case 'DefaultDate':
                    tempFilterArr.push(`r.date > (now() - interval '30 day')`);
                    break;
                  default:
                    const dateArray: string[] = element.split(' - ');
                    if (dateArray.length === 2) {
                      tempFilterArr.push(
                        `reviewCreatedAt >= '${dateArray[0]}' AND reviewCreatedAt <= '${dateArray[1]}'`
                      );
                    }
                    break;
                }
              });
              pushQueriesToMainArray();
              break;
            case 'REVIEW TEXT':
              value.forEach((element: string) => {
                element === 'Review includes text'
                  ? tempFilterArr.push('reviewercomment IS NOT NULL')
                  : tempFilterArr.push('reviewercomment IS NULL');
              });
              pushQueriesToMainArray();
              break;
          }
        }
      }
    } else {
      this.selectedDateRange = 'DefaultDate';
      tempFilterArr.push(`r.date > (now() - interval '30 day')`);
      pushQueriesToMainArray();
    }

    if (this.keyword) {
      tempFilterArr.push(`storeCode::text ~* '\\y${this.keyword}'`);
      tempFilterArr.push(`businessName::text ~* '\\y${this.keyword}'`);
      tempFilterArr.push(`tags::text ~* '\\y${this.keyword}'`);
      tempFilterArr.push(`addressLines::text ~* '\\y${this.keyword}'`);
      tempFilterArr.push(`city::text ~* '\\y${this.keyword}'`);
      tempFilterArr.push(`state::text ~* '\\y${this.keyword}'`);
      tempFilterArr.push(`postalCode::text ~* '\\y${this.keyword}'`);
      tempFilterArr.push(`reviewerComment::text ~* '\\y${this.keyword}'`);
      tempFilterArr.push(`replyComment::text ~* '\\y${this.keyword}'`);
      pushQueriesToMainArray();
    }

    const whereClauseArray: Array<any> = [
      '-and',
      "r._status IN ('A', 'UA', 'UP')",
      "vendorIdent='gmb'",
      ...filterClauses,
    ];
    return whereClauseArray;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
